import {Injectable} from '@angular/core';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {ReportType} from '../models/report-type.model';
import {
  CALENDAR_MONTH_OFFSET,
  DATE_DAY_PART_INDEX,
  DATE_MONTH_PART_INDEX,
  DATE_PART_SEPARATOR,
  DATE_YEAR_PART_INDEX,
  DECEMBER_INDEX,
  FIRST_DAY_OF_MONTH,
  FIRST_MONTH,
  INITIAL_DATE_PART,
  JANUARY_INDEX,
  LAST_MONTH,
  MINIMUM_EXPERIENCE_YEAR,
  NO_REPORT_MARKER,
  NOVEMBER_INDEX,
  STANDARD_REPORT_TAB
} from './report-datepicker.constants';
import {
  DateRange,
  ReportDateDefaults,
  ReportDatepickerDateView,
  ShiftSchedule
} from './report-datepicker.types';

@Injectable()
export class ReportDatepickerDateCoordinator {
  initializeCalendarBounds(view: ReportDatepickerDateView): void {
    const current = new Date();
    view.dateCurrent = this.dateStruct(current);
    view.dateStart = {
      year: current.getFullYear(), month: current.getMonth() + CALENDAR_MONTH_OFFSET,
      day: FIRST_DAY_OF_MONTH
    };
    view.minDateForExpirence = {
      year: MINIMUM_EXPERIENCE_YEAR, month: FIRST_MONTH, day: FIRST_DAY_OF_MONTH
    };
  }

  initializeReportDates(view: ReportDatepickerDateView): void {
    const today = new Date();
    const fromDate = this.emptyDateRange();
    const toDate = this.emptyDateRange();
    this.calculateReportDateRanges(today, fromDate, toDate);
    view.today = new Date();
    const lastDay = new Date(toDate.year, toDate.month, INITIAL_DATE_PART).getDate();
    const previousLastDay = new Date(
      toDate.year, toDate.month - CALENDAR_MONTH_OFFSET, INITIAL_DATE_PART).getDate();
    const defaults = {
      today: today, fromDate: fromDate, toDate: toDate,
      lastDay: lastDay, previousLastDay: previousLastDay
    };
    this.applyReportTypeDates(view, defaults);
    this.initializeShiftReport(view, defaults);
  }

  updateFrom(view: ReportDatepickerDateView, date: NgbDateStruct): void {
    view.from = date;
    const lastDay = new Date(date.year, date.month, INITIAL_DATE_PART).getDate();
    if (!view.to || view.to.year !== date.year || view.to.month !== date.month) {
      view.to = {year: date.year, month: date.month, day: lastDay};
    }
  }

  updateTo(view: ReportDatepickerDateView, date: NgbDateStruct): void {
    view.to = date;
    if (!view.from || view.from.year !== date.year || view.from.month !== date.month) {
      view.from = {year: date.year, month: date.month, day: FIRST_DAY_OF_MONTH};
    }
  }

  isFromAfterToday(view: ReportDatepickerDateView): boolean {
    if (view.from) {
      return new Date(this.serializedDate(view.from)) > new Date(view.today);
    }
  }

  isRangeValid(view: ReportDatepickerDateView): boolean {
    if (view.from) {
      return new Date(this.serializedDate(view.to)).getTime() >=
        new Date(this.serializedDate(view.from)).getTime();
    }
  }

  isCurrentShiftRange(
    view: ReportDatepickerDateView, shift: number, stateShift: number
  ): boolean {
    if (!view.from) {
      return undefined;
    }
    const sameFrom = new Date(view.currentFrom).getTime() ===
      new Date(this.serializedDate(view.from)).getTime();
    const sameTo = new Date(view.currentTo).getTime() ===
      new Date(this.serializedDate(view.to)).getTime();
    return shift == stateShift && sameFrom && sameTo;
  }

  private applyReportTypeDates(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults
  ): void {
    if (view.reportType === ReportType.BY_VACATION) {
      this.setPreviousMonthRange(view, defaults, defaults.lastDay);
    }
    if (view.groupName || view.reportType === ReportType.BY_USER_DEP) {
      this.setPreviousMonthRange(view, defaults, defaults.previousLastDay);
      return;
    }
    this.setCurrentMonthRange(view, defaults, this.reportStartDay(view, defaults));
  }

  private reportStartDay(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults
  ): number {
    return view.rep === NO_REPORT_MARKER ||
      view.reportType === ReportType.BY_KARMA ||
      view.reportType === ReportType.BY_PROFILE
      ? defaults.today.getDate() : FIRST_DAY_OF_MONTH;
  }

  private setCurrentMonthRange(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults, startDay: number
  ): void {
    view.from = {
      year: defaults.fromDate.year, month: defaults.toDate.month, day: startDay
    };
    view.to = {
      year: defaults.toDate.year, month: defaults.toDate.month, day: defaults.lastDay
    };
  }

  private initializeShiftReport(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults
  ): void {
    if (view.reportType === ReportType.SHIFT) {
      this.applyCurrentShift(view, defaults);
      if (view.shiftPlanned) {
        this.applyPlannedShift(view, defaults);
      }
    }
    this.normalizePreviousYear(view);
    if (view.reportType === ReportType.SHIFT && view.currentTab === STANDARD_REPORT_TAB) {
      view.from = view.dateCurrent;
    }
  }

  private applyPlannedShift(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults
  ): void {
    if (view.shiftPlanned.from && view.shiftPlanned.to) {
      this.applyStoredShiftRange(view, view.shiftPlanned);
      return;
    }
    this.applyMissingShiftRange(view, defaults);
  }

  private applyCurrentShift(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults
  ): void {
    if (!view.shiftCurrent) {
      return;
    }
    if (view.shiftCurrent.from && view.shiftCurrent.to) {
      this.applyStoredShiftRange(view, view.shiftCurrent);
      return;
    }
    this.applyMissingShiftRange(view, defaults);
  }

  private applyStoredShiftRange(
    view: ReportDatepickerDateView, shift: ShiftSchedule
  ): void {
    const from = shift.from.split(DATE_PART_SEPARATOR);
    const to = shift.to.split(DATE_PART_SEPARATOR);
    view.currentFrom = shift.from;
    view.currentTo = shift.to;
    view.from = {
      year: Number(from[DATE_YEAR_PART_INDEX]), month: Number(from[DATE_MONTH_PART_INDEX]),
      day: Number(from[DATE_DAY_PART_INDEX])
    };
    view.to = {
      year: Number(to[DATE_YEAR_PART_INDEX]), month: Number(to[DATE_MONTH_PART_INDEX]),
      day: Number(to[DATE_DAY_PART_INDEX])
    };
  }

  private applyMissingShiftRange(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults
  ): void {
    view.checkValue = false;
    view.currenValue = true;
    view.current = false;
    view.from = {
      year: defaults.fromDate.year, month: defaults.toDate.month,
      day: defaults.today.getDate() + CALENDAR_MONTH_OFFSET
    };
    view.to = {
      year: defaults.toDate.year, month: defaults.toDate.month, day: defaults.lastDay
    };
  }

  private calculateReportDateRanges(
    today: Date, fromDate: DateRange, toDate: DateRange
  ): void {
    fromDate.year = today.getFullYear();
    toDate.year = today.getFullYear();
    if (today.getMonth() === JANUARY_INDEX) {
      fromDate.month = LAST_MONTH;
      toDate.month = today.getMonth() + CALENDAR_MONTH_OFFSET;
    } else if (today.getMonth() <= NOVEMBER_INDEX) {
      fromDate.month = today.getMonth();
      toDate.month = today.getMonth() + CALENDAR_MONTH_OFFSET;
    } else if (today.getMonth() === DECEMBER_INDEX) {
      fromDate.month = today.getMonth();
      toDate.month = today.getMonth() + CALENDAR_MONTH_OFFSET;
    }
  }

  private normalizePreviousYear(view: ReportDatepickerDateView): void {
    if (view.from.month === INITIAL_DATE_PART || view.to.month === INITIAL_DATE_PART) {
      view.from.month = LAST_MONTH;
      view.from.year = view.from.year - CALENDAR_MONTH_OFFSET;
      view.to.month = LAST_MONTH;
      view.to.year = view.to.year - CALENDAR_MONTH_OFFSET;
    }
  }

  private setPreviousMonthRange(
    view: ReportDatepickerDateView, defaults: ReportDateDefaults, lastDay: number
  ): void {
    view.from = {
      year: defaults.fromDate.year,
      month: defaults.toDate.month - CALENDAR_MONTH_OFFSET,
      day: FIRST_DAY_OF_MONTH
    };
    view.to = {
      year: defaults.toDate.year,
      month: defaults.toDate.month - CALENDAR_MONTH_OFFSET,
      day: lastDay
    };
  }

  private dateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(), month: date.getMonth() + CALENDAR_MONTH_OFFSET,
      day: date.getDate()
    };
  }

  private emptyDateRange(): DateRange {
    return {year: INITIAL_DATE_PART, month: INITIAL_DATE_PART};
  }

  private serializedDate(date: NgbDateStruct): string {
    return date.year + DATE_PART_SEPARATOR + date.month + DATE_PART_SEPARATOR + date.day;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/modals/report-datepicker-date.coordinator.ts